import { AutoMatchDeposit } from '@/context/NetworkContext';
import * as yup from 'yup';

export const autoMatchDepositSchema = yup.object().shape({
  enabled: yup.boolean().nullable(),
  agentID: yup
    .string()
    .nullable()
    .when('enabled', {
      is: true,
      then: (schema) =>
        schema
          .min(3, 'Agent ID must be at least 3 characters.')
          .required('Agent ID is required.'),
      otherwise: (schema) => schema.nullable(),
    }),
  groups: yup
    .array()
    .of(
      yup.object().shape({
        name: yup
          .string()
          .min(3, 'Name must be at least 3 characters')
          .required('Name is required'),
        percentage: yup
          .number()
          .moreThan(0, 'Percentage must be greater than 0')
          .max(100, 'Percentage must be less than 100')
          .required('Percentage is required'),
        agents: yup.array().min(1, 'At least one agent is required'),
        minTransferAmount: yup
          .number()
          .moreThan(0, 'Min Transfer Amount must be greater than 0')
          .test(
            'min-less-than-max',
            'Min Transfer Amount must be lower than Max Transfer Amount.',
            function (minTransferAmount) {
              if (minTransferAmount == null) return true; // Skip if null

              const { maxTransferAmount } = this.parent;
              if (maxTransferAmount == null) return true; // Skip if max is null

              return minTransferAmount < maxTransferAmount;
            }
          ),
        maxTransferAmount: yup
          .number()
          .moreThan(0, 'Max Transfer Amount must be greater than 0')
          .test(
            'max-greater-than-min',
            'Max Transfer Amount must be greater than Min Transfer Amount.',
            function (maxTransferAmount) {
              if (maxTransferAmount == null) return true; // Skip if null

              const { minTransferAmount } = this.parent;
              if (minTransferAmount == null) return true; // Skip if min is null

              return maxTransferAmount > minTransferAmount;
            }
          ),
        minDepositAmount: yup
          .number()
          .moreThan(0, 'Min Deposit Amount must be greater than 0'),
      })
    )
    .test(
      'required-if-enabled',
      'At least one group is required when Auto Match Deposit is enabled',
      function (groups) {
        // Get the parent object which contains the enabled field
        const { enabled } = this.parent;

        // If enabled is true, require at least one group
        if (enabled) {
          return groups && groups.length > 0;
        }

        // If not enabled, groups can be empty
        return true;
      }
    ),
  defaultGroup: yup.number().nullable().default(null),
  startDay: yup
    .number()
    .nullable()
    .when('enabled', {
      is: true,
      then: (schema) =>
        schema
          .min(0, 'Invalid start day.')
          .max(6, 'Invalid start day.')
          .required('Start day is required.')
          .test(
            'start-not-equal-end',
            'Start day and End day cannot be the same.',
            function (startDay) {
              const { endDay } = this.parent;
              return endDay !== startDay;
            }
          ),
      otherwise: (schema) => schema.nullable(),
    }),
  endDay: yup
    .number()
    .nullable()
    .when('enabled', {
      is: true,
      then: (schema) =>
        schema
          .min(0, 'Invalid end day.')
          .max(6, 'Invalid end day.')
          .required('End day is required.')
          .test(
            'start-not-equal-end',
            'Start day and End day cannot be the same.',
            function (endDay) {
              const { startDay } = this.parent;
              return startDay !== endDay;
            }
          ),
      otherwise: (schema) => schema.nullable(),
    }),
});

export const roleOptions = [
  { value: 'manager', label: 'Manager' },
  { value: 'user', label: 'Agent' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

export const applyAutoMatchDepositFallbacks = (
  autoMatchDeposit: AutoMatchDeposit
) => {
  if (!autoMatchDeposit || !autoMatchDeposit?.groups) {
    return autoMatchDeposit;
  }

  const {
    minTransferAmount,
    maxTransferAmount,
    minDepositAmount,
  } = autoMatchDeposit;

  // Apply fallbacks to each group
  const updatedGroups = autoMatchDeposit?.groups?.map((group) => ({
    ...group,
    minTransferAmount: group?.minTransferAmount
      ? group?.minTransferAmount
      : minTransferAmount,
    maxTransferAmount: group?.maxTransferAmount
      ? group?.maxTransferAmount
      : maxTransferAmount,
    minDepositAmount: group?.minDepositAmount
      ? group?.minDepositAmount
      : minDepositAmount,
  }));

  return {
    ...autoMatchDeposit,
    groups: updatedGroups,
  };
};
